import React, { useState, useEffect, useMemo } from 'react'
import { useConfigStore } from '@renderer/stores/configState'
import { usePrintStore } from '@renderer/stores/printStore'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import { useTranslation } from 'react-i18next'
import Button from '@renderer/components/Button'
import type { CategoryClaim, SlotId } from '@renderer/utils/printRouting'

interface FrameSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  selectedFrames: string[]
  /** Category claims already on this slot. Rendered only when the flag is on. */
  selectedCategories?: CategoryClaim[]
  /**
   * Legacy callback — frame ids only. Still fired so older callers keep
   * working; new callers should use onSelectionChange.
   */
  onFramesSelected: (frameIds: string[]) => void
  /** Full result: explicit frames plus category claims. */
  onSelectionChange?: (frameIds: string[], categories: CategoryClaim[]) => void
  printerType?: 'default' | 'secondary' | 'tertiary'
}

const SLOT_CHIP: Record<SlotId, string> = { default: 'P', secondary: 'S', tertiary: 'T' }

export const FrameSelectionModal: React.FC<FrameSelectionModalProps> = ({
  isOpen,
  onClose,
  selectedFrames,
  selectedCategories = [],
  onFramesSelected,
  onSelectionChange,
  printerType = 'secondary'
}) => {
  const { t } = useTranslation()
  const { frames, frameCategories } = useConfigStore()
  const { defaultRouting, secondaryPrinter, tertiaryPrinter } = usePrintStore()
  const multiClaim = useFeatureFlagsStore((s) => s.getFlag('printer_multi_claim_routing'))

  const [tempSelectedFrames, setTempSelectedFrames] = useState<string[]>(selectedFrames)
  const [tempCategories, setTempCategories] = useState<CategoryClaim[]>(selectedCategories)

  // Critical: re-seed local state when the slot being edited changes, so
  // switching between tabs never shows the previous slot's selection.
  useEffect(() => {
    setTempSelectedFrames([...selectedFrames])
    setTempCategories(selectedCategories.map((c) => ({ ...c })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFrames, printerType, isOpen])

  /**
   * Which OTHER slots claim each frame — powers the P/S/T chips so an operator
   * sees an overlap at the moment they are creating it. Pre-resolution claims,
   * like PrintPlan.claimants: the catch-all default counts.
   */
  const otherClaimants = useMemo(() => {
    if (!multiClaim) return new Map<string, SlotId[]>()
    const map = new Map<string, SlotId[]>()
    const liveIds = (name: string): string[] =>
      frameCategories.find((c) => c.name === name)?.ids ?? []
    const claimedBy = (
      slotId: SlotId,
      routing:
        | { allFrames: boolean; frameIds: string[]; categories: CategoryClaim[] }
        | undefined,
      hasPrinter: boolean,
      frameId: string
    ): boolean => {
      if (slotId === printerType) return false // chips show OTHER slots only
      if (!hasPrinter) return false
      if (!routing) return slotId === 'default' // unmigrated default = catch-all
      if (routing.allFrames) return true
      if (routing.frameIds.includes(frameId)) return true
      return routing.categories.some((c) =>
        (liveIds(c.name).length ? liveIds(c.name) : c.frameIdsSnapshot).includes(frameId)
      )
    }
    for (const frame of frames) {
      const holders: SlotId[] = []
      if (claimedBy('default', defaultRouting, true, frame._id)) holders.push('default')
      if (
        claimedBy('secondary', secondaryPrinter?.routing, !!secondaryPrinter?.printerName, frame._id)
      )
        holders.push('secondary')
      if (
        claimedBy('tertiary', tertiaryPrinter?.routing, !!tertiaryPrinter?.printerName, frame._id)
      )
        holders.push('tertiary')
      if (holders.length) map.set(frame._id, holders)
    }
    return map
  }, [
    multiClaim,
    frames,
    frameCategories,
    defaultRouting,
    secondaryPrinter,
    tertiaryPrinter,
    printerType
  ])

  /** Frames covered by a ticked category — shown checked but inherited. */
  const inheritedFrameIds = useMemo(() => {
    const ids = new Set<string>()
    for (const claim of tempCategories) {
      const live = frameCategories.find((c) => c.name === claim.name)
      for (const id of live?.ids ?? claim.frameIdsSnapshot) ids.add(id)
    }
    return ids
  }, [tempCategories, frameCategories])

  if (!isOpen) return null

  const handleFrameToggle = (frameId: string): void => {
    if (inheritedFrameIds.has(frameId)) return // owned by the category claim
    setTempSelectedFrames((prev) =>
      prev.includes(frameId) ? prev.filter((id) => id !== frameId) : [...prev, frameId]
    )
  }

  const handleCategoryToggle = (name: string): void => {
    setTempCategories((prev) => {
      if (prev.some((c) => c.name === name)) return prev.filter((c) => c.name !== name)
      const live = frameCategories.find((c) => c.name === name)
      // The snapshot is what keeps this claim routing after an offline boot or
      // a Studio rename — capture it at claim time.
      return [...prev, { name, frameIdsSnapshot: [...(live?.ids ?? [])], resolvedAt: Date.now() }]
    })
  }

  const titleKey =
    printerType === 'tertiary'
      ? 'printerSettings.selectFramesTertiary'
      : printerType === 'default'
        ? 'printerSettings.selectFramesDefault'
        : 'printerSettings.selectFrames'
  const descKey =
    printerType === 'tertiary'
      ? 'printerSettings.selectFramesDescriptionTertiary'
      : printerType === 'default'
        ? 'printerSettings.selectFramesDescriptionDefault'
        : 'printerSettings.selectFramesDescription'

  const showCategories = multiClaim && frameCategories.length > 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold">{t(titleKey)}</h2>
          <p className="text-gray-600 mt-2">{t(descKey)}</p>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {showCategories && (
            <div>
              <h3 className="font-semibold mb-2">
                {t('printerSettings.routing.categories', 'Categories')}
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                {t(
                  'printerSettings.routing.categoriesHint',
                  'Claiming a category includes every frame in it — also frames added to the category later.'
                )}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {frameCategories.map((cat) => {
                  const claimed = tempCategories.some((c) => c.name === cat.name)
                  return (
                    <div
                      key={cat.name}
                      className={`border rounded-lg p-3 cursor-pointer transition-all flex items-center gap-3 ${
                        claimed
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => handleCategoryToggle(cat.name)}
                    >
                      <input
                        type="checkbox"
                        checked={claimed}
                        onChange={() => handleCategoryToggle(cat.name)}
                        className="h-5 w-5 text-blue-600 border-gray-300 rounded"
                        title={cat.name}
                      />
                      <div>
                        <div className="font-medium">{cat.name}</div>
                        <div className="text-xs text-gray-500">
                          {t('printerSettings.routing.frameCount', '{{count}} frames', {
                            count: (cat.ids || []).length
                          })}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div>
            {showCategories && (
              <h3 className="font-semibold mb-2">
                {t('printerSettings.routing.individualFrames', 'Individual frames')}
              </h3>
            )}
            <div className="grid grid-cols-2 gap-4">
              {frames.map((frame) => {
                const inherited = inheritedFrameIds.has(frame._id)
                const checked = inherited || tempSelectedFrames.includes(frame._id)
                const chips = otherClaimants.get(frame._id) ?? []
                return (
                  <div
                    key={frame._id}
                    className={`border rounded-lg p-4 transition-all ${
                      inherited
                        ? 'border-blue-300 bg-blue-50/50 cursor-default'
                        : checked
                          ? 'border-blue-500 bg-blue-50 cursor-pointer'
                          : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                    }`}
                    onClick={() => handleFrameToggle(frame._id)}
                  >
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={inherited}
                        onChange={() => handleFrameToggle(frame._id)}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        title={frame.name}
                      />
                      <img
                        src={frame.thumbnail}
                        alt={frame.name}
                        className="w-20 h-20 object-cover rounded"
                      />
                      <div className="min-w-0">
                        <h3 className="font-medium truncate">{frame.name}</h3>
                        {inherited && (
                          <div className="text-xs text-blue-600">
                            {t('printerSettings.routing.viaCategory', 'Included via category')}
                          </div>
                        )}
                        {chips.length > 0 && (
                          <div className="flex gap-1 mt-1 items-center">
                            {chips.map((slot) => (
                              <span
                                key={slot}
                                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-[10px] font-bold text-gray-700"
                                title={slot}
                              >
                                {SLOT_CHIP[slot]}
                              </span>
                            ))}
                            {checked && (
                              <span className="text-[10px] text-amber-600 font-medium">
                                {t(
                                  'printerSettings.routing.sharedBadge',
                                  'Also on another printer'
                                )}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <Button
            text={t('common.cancel', 'Cancel')}
            variant="secondary"
            onClick={onClose}
            className="!px-6 !py-2 !text-base"
          />
          <Button
            text={t('common.save', 'Save')}
            onClick={() => {
              onFramesSelected([...tempSelectedFrames])
              onSelectionChange?.(
                [...tempSelectedFrames],
                tempCategories.map((c) => ({ ...c, frameIdsSnapshot: [...c.frameIdsSnapshot] }))
              )
              onClose()
            }}
            className="!px-6 !py-2 !text-base"
          />
        </div>
      </div>
    </div>
  )
}
